import { useApiIsLoaded } from '@vis.gl/react-google-maps'
import { observer } from 'mobx-react'
import React, { useEffect } from 'react'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Fab from '@mui/material/Fab'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useQuery } from '../models'
import FilterChipBar from './FilterChipBar'
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete'
import ResultsPanel from './LazyResultsPanel'
import MapContainer from './MapContainer'
import MobileLocationDetails from './MobileLocationDetails'
import MobileShowListButton from './MobileShowListButton'
import ProviderDetailsPanel from './ProviderDetailsPanel'

const MainContent = () => {
  const { store } = useQuery()
  const theme = useTheme()
  const desktop = useMediaQuery(theme.breakpoints.up('md'))
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, toggleDrawer] = React.useState(false)
  const [selectedProvider, setSelectedProvider] = React.useState(null)
  const [providerDetailsLoading, setProviderDetailsLoading] =
    React.useState(false)
  const [providerDetailsError, setProviderDetailsError] = React.useState(false)
  const [selectedLocation, setSelectedLocation] = React.useState(null)
  const drawerWidth = desktop ? '408px' : 'auto'
  const searchBarExpanded = store.searchStore.searchBarExpanded
  const staticPage = store.staticPage
  const isLoaded = useApiIsLoaded()
  const mobileSheetTop = staticPage ? 0 : searchBarExpanded ? 148 : 92

  const handleProviderSelect = async provider => {
    setSelectedProvider(provider)
    setProviderDetailsError(false)

    if (provider.bio !== undefined && provider.npiNumber !== undefined) {
      return
    }

    setProviderDetailsLoading(true)

    try {
      await store.fetchProviderDetails(provider.id)
    } catch (error) {
      store.debug(`Unable to fetch provider details: ${error.message}`)
      setProviderDetailsError(true)
    } finally {
      setProviderDetailsLoading(false)
    }
  }

  useEffect(() => {
    if (store.shouldDrawerBeOpen && desktop) {
      toggleDrawer(true)
    }
  }, [store.shouldDrawerBeOpen, desktop])

  const handleDrawerToggle = open => {
    toggleDrawer(open)
    if (!open) {
      setSelectedProvider(null)
      setSelectedLocation(null)
    }
  }

  return (
    <Box display="flex" aria-label="Main Content Container">
      <Drawer
        variant="persistent"
        anchor={desktop ? 'left' : 'bottom'}
        open={drawerOpen}
        onClose={() => handleDrawerToggle(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            ...(!desktop && {
              zIndex: 1202,
              top: mobileSheetTop,
              bottom: 0,
              height: 'auto',
              maxHeight: 'none',
              borderRadius: 0,
              borderTop: 0,
              boxShadow: 'none',
              overflow: 'hidden',
              '--mobile-sheet-height':
                'calc(100dvh - ' + mobileSheetTop + 'px)',
            }),
          },
        }}
      >
        <React.Suspense fallback={<div></div>}>
          {!desktop && selectedProvider ? (
            <ProviderDetailsPanel
              key={selectedProvider.id}
              provider={selectedProvider}
              loading={providerDetailsLoading}
              error={providerDetailsError}
              onRetry={() => handleProviderSelect(selectedProvider)}
              mobile
              onClose={() => setSelectedProvider(null)}
              onLocationSelect={location => {
                setSelectedProvider(null)
                setSelectedLocation(location)
              }}
              onShowLocation={() => handleDrawerToggle(false)}
            />
          ) : !desktop && selectedLocation ? (
            <MobileLocationDetails
              key={selectedLocation.id}
              location={selectedLocation}
              onBack={() => setSelectedLocation(null)}
              onProviderSelect={handleProviderSelect}
              onShowLocation={() => handleDrawerToggle(false)}
            />
          ) : null}
          <div
            hidden={!desktop && !!(selectedProvider || selectedLocation)}
            style={{ height: desktop ? undefined : '100%' }}
          >
            <ResultsPanel
              toggleDrawer={handleDrawerToggle}
              selectedProvider={selectedProvider}
              onProviderSelect={handleProviderSelect}
              onLocationSelect={!desktop ? setSelectedLocation : undefined}
            />
          </div>
        </React.Suspense>
      </Drawer>
      {desktop && drawerOpen && selectedProvider ? (
        <ProviderDetailsPanel
          provider={selectedProvider}
          loading={providerDetailsLoading}
          error={providerDetailsError}
          onRetry={() => handleProviderSelect(selectedProvider)}
          onClose={() => setSelectedProvider(null)}
        />
      ) : null}
      <React.Suspense fallback={<div></div>}>
        {mobile && !staticPage ? (
          <Box
            aria-label="Mobile Search Panel"
            boxShadow={2}
            sx={{
              position: 'absolute',
              width: '100vw',
              height: searchBarExpanded ? 160 : 104,
              bgcolor: '#FFF',
              zIndex: 1200,
            }}
          />
        ) : null}
        <Box aria-label="Search Panel">
          <Box
            aria-label="Search Bar"
            boxShadow={desktop && !drawerOpen ? 3 : 0}
            sx={{
              position: 'absolute',
              zIndex: desktop ? 1230 : 1220,
              top: mobile ? 12 : 16,
              left: staticPage ? 'none' : mobile ? 'none' : 24,
              width: mobile ? 'fill-available' : 'fit-content',
              bgcolor: 'background.paper',
              borderRadius: '24px',
              border: desktop && drawerOpen ? 2 : 0,
              borderColor: desktop && drawerOpen ? 'primary.main' : 'none',
              mx: mobile ? 1.5 : 0,
              ...(mobile && {
                boxShadow: '0 0 0 .5px #8c4799',
                '& .MuiInputBase-root': {
                  fontFamily: 'Qanelas, Arial, sans-serif',
                  fontSize: 14,
                },
              }),
            }}
          >
            {isLoaded ? <GooglePlacesAutocomplete /> : null}
          </Box>
          <Box
            aria-label="Search Filters"
            sx={{
              position: 'absolute',
              zIndex: desktop ? 1220 : 1210,
              top: mobile ? (searchBarExpanded ? 124 : 68) : 14,
              left: staticPage ? 'none' : mobile ? 'none' : 430,
              width: 'fill-available',
              overflowX: 'auto',
              webkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              py: desktop ? 1 : '2px',
            }}
          >
            <FilterChipBar />
          </Box>
        </Box>
      </React.Suspense>
      {store.canShowResultsPanel && desktop ? (
        <Tooltip title="Toggle Side Panel" placement="right" arrow>
          <Fab
            size="small"
            onClick={() => handleDrawerToggle(!drawerOpen)}
            color="primary"
            sx={{
              position: 'absolute',
              top: '50vh',
              left: drawerOpen ? '408px' : '0',
              zIndex: 1300,
              boxShadow: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8,
              width: 24,
            }}
          >
            {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </Fab>
        </Tooltip>
      ) : null}
      <React.Suspense fallback={<div></div>}>
        {!drawerOpen && (
          <MobileShowListButton
            isDesktop={desktop}
            toggleDrawer={handleDrawerToggle}
          />
        )}
      </React.Suspense>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: desktop && drawerOpen ? drawerWidth : 0,
          height: '100vh',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <MapContainer toggleDrawer={handleDrawerToggle} />
      </Box>
    </Box>
  )
}

export default observer(MainContent)
